import { useEffect, useRef } from 'react';

/**
 * The behaviour every popover panel in this app owes its reader: Escape
 * closes it, a click anywhere else closes it, and focus goes in on open and
 * comes back to the trigger on close.
 *
 * Extracted from `NotificationMenu`, which had all of it inline, at the
 * moment a second panel needed the same thing. Not before — a hook written
 * for one call site is a guess about the second. The two it has now disagree
 * about everything else (one polls a server, the other holds three form
 * controls) and about none of this.
 *
 * Both listeners are registered in the CAPTURE phase. A control inside the
 * panel may legitimately stop a bubbling event — a slider swallowing its own
 * arrow keys, say — and dismissal must not be something an inner control can
 * quietly break by doing that.
 */
export function useDismissable<Trigger extends HTMLElement, Panel extends HTMLElement>(
  open: boolean,
  close: () => void,
) {
  const triggerRef = useRef<Trigger>(null);
  const panelRef = useRef<Panel>(null);

  // `close` is usually an inline arrow, so it is a new function every render.
  // Kept in a ref rather than in the dependency list, or the listeners would
  // be torn down and re-attached on every keystroke the panel handles.
  const closeRef = useRef(close);
  closeRef.current = close;

  useEffect(() => {
    if (!open) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      // Stopped so one Escape closes one layer. A panel inside a dialog must
      // not take the dialog down with it.
      event.stopPropagation();
      closeRef.current();
    }

    function handlePointerDown(event: MouseEvent) {
      const target = event.target as Node;
      if (panelRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      closeRef.current();
    }

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('mousedown', handlePointerDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('mousedown', handlePointerDown, true);
    };
  }, [open]);

  /**
   * Focus moves into the panel on open, and back to the trigger on close —
   * but only on a REAL close, never on the first render.
   *
   * `open` starts false, so an unguarded effect runs the "closed" branch on
   * mount and pulls focus to the trigger the moment the page loads, stealing
   * it from wherever the reader actually was. That bug was found once
   * already, in the menu this came out of; the guard travels with the
   * behaviour so it cannot be lost the next time somebody writes a panel.
   */
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open) {
      panelRef.current?.focus();
    } else if (wasOpen.current) {
      triggerRef.current?.focus();
    }
    wasOpen.current = open;
  }, [open]);

  return { triggerRef, panelRef };
}
